// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.RoleDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { action, observable, computed } from 'mobx';
import PermissionDetail from 'logic/models/Izenda.PermissionDetail';
import PermissionAccess from './Izenda.Access';
import tracker from 'share/tracking/Izenda.Tracker';
import { cloneDeep, omit, uniqueId } from 'lodash/fp';
import { ROLE_TYPES } from 'logic/data/Izenda.RoleSetupConstants';

@tracker
export default class RoleDetail {
  @observable key: string = null;
  @observable users: Array<any> = [];
  @observable permission: PermissionDetail = new PermissionDetail(null, false);
  @observable visibleQuerySources: Array<any> = [];
  @observable name: string = null;
  @observable tenantId: string = null;
  @observable active: boolean = true;
  @observable deleted: boolean = false;
  @observable id: string = null;
  @observable state: number = 0;
  @observable inserted: boolean = false;
  @observable version: number = 0;
  @observable created: any = null;
  @observable createdBy: string = '';
  @observable modified: any = null;
  @observable modifiedBy: string = '';
  @observable roleType: string = ROLE_TYPES.Administrator;

  @computed
  get hasChange() {
    return this.isDirty || this.permission.isDirty || this.permissionAccessModel.checkDirty();
  }

  @computed
  get isNew() {
    return !String.isGuidFormat(this.id);
  }

  @computed
  get isTemplateRole() {
    return this.roleType !== ROLE_TYPES.Custom;
  }

  constructor(data: any) {
    this.permissionAccessModel = new PermissionAccess();
    this.basedProps = {
      users: [],
      name: null,
      active: true,
      id: null
    };
    data && this.populateData(data);
  }

  @action
  changeRole() {
    this.key = uniqueId('RoleDetail_');
  }

  @action
  setRoleType(roleType: string) {
    this.roleType = roleType;
    this.markDirty();
  }

  @action
  toggleActive() {
    this.active = !this.active;
  }

  @action
  resetData(tenantId: string, tenantDetail: any) {
    this.populateData(cloneDeep(this.basedProps));
    this.tenantId = tenantId;

    // Get tenant data and populate to role.
    tenantDetail && this.permission.populateData(tenantDetail, this.permission.propsCloned, this.permission);
  }

  /**
   * Populate data
   * @param {Object} data
   */
  @action
  populateData(data: any) {
    this.users = data.users;
    this.permission.populateData(data.permission, this.permission.propsCloned, this.permission);
    this.visibleQuerySources = data.visibleQuerySources;
    this.name = data.name;
    this.tenantId = data.tenantId;
    this.active = data.active;
    this.deleted = data.deleted;
    this.id = data.id;
    this.state = data.state;
    this.inserted = data.inserted;
    this.version = data.version;
    this.created = data.created;
    this.createdBy = data.createdBy;
    this.modified = data.modified;
    this.modifiedBy = data.modifiedBy;
    this.roleType = data.roleType;
    this.resetDirty();
  }

  @action
  populatePermissionTemplate(data: Object) {
    this.permission.populateData(data.permission, this.permission.propsCloned, this.permission);
  }

  /**
   * update name of role
   */
  @action
  updateName(newName: string) {
    this.name = newName;
    this.markDirty();
  }

  /**
   * Set Visible QuerySources
   * @param {Object} visibleQuerySources
   */
  @action
  setVisibleQuerySources(visibleQuerySources: Array<any>) {
    this.visibleQuerySources = visibleQuerySources;
    this.markDirty();
  }

  /**
   * Prepare Data For Save
   * @return {Object}
   */
  prepareDataForSave() {
    const data = omit(['id', 'basedProps', 'permissionAccessModel', 'key'], this);
    if (!this.isNew) {
      data.id = this.id;
    }

    return data;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.RoleDetail.js